import { useTranslation } from "react-i18next";
import { Split } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { formatNumber } from "../../../../utils";
import { StockStatusCodeBadge } from "../../components/StockStatusBadges";
import { SplitInfo } from "../types";
import { formatDate, newestFirst } from "../utils";
import { DetailList } from "./DetailList";

interface SplitTraceabilityProps {
  split: SplitInfo;
  unit?: string;
  pagePath: (uuid: string) => string;
}

export const SplitTraceability = ({ split, unit, pagePath }: SplitTraceabilityProps) => {
  const { t } = useTranslation();
  if (!split.is_split) {
    return null;
  }
  const quantity = (value: string | undefined) =>
    value === undefined ? "–" : unit ? `${formatNumber(value)} ${unit}` : formatNumber(value);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Split className="size-4 text-primary" />
          {t("farmersMarket.split.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <DetailList
          items={[
            {
              label: t("farmersMarket.split.parent"),
              value: split.parent ? (
                <Link className="text-primary hover:underline" to={pagePath(split.parent.uuid)}>
                  #{split.parent.id}
                </Link>
              ) : (
                t("farmersMarket.split.isOriginal")
              ),
            },
            {
              label: t("farmersMarket.split.originalQuantity"),
              value: quantity(split.original_quantity),
            },
            { label: t("farmersMarket.split.sold"), value: quantity(split.quantity_sold) },
            {
              label: t("farmersMarket.split.salePending"),
              value: quantity(split.quantity_sale_pending),
            },
            {
              label: t("farmersMarket.split.remaining"),
              value: quantity(split.quantity_remaining),
            },
            { label: t("farmersMarket.split.removed"), value: quantity(split.quantity_removed) },
            { label: t("farmersMarket.split.adjusted"), value: quantity(split.quantity_adjusted) },
          ]}
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("farmersMarket.split.stock")}</TableHead>
              <TableHead>{t("farmersMarket.split.quantity")}</TableHead>
              <TableHead>{t("farmersMarket.split.status")}</TableHead>
              <TableHead>{t("farmersMarket.split.createdAt")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {newestFirst(split.pieces, piece => piece.created_at).map(piece => (
              <TableRow key={piece.uuid}>
                <TableCell>
                  {piece.is_this_stock ? (
                    <span className="flex items-center gap-2 font-medium">
                      #{piece.id}
                      <Badge variant="outline">{t("farmersMarket.split.thisStock")}</Badge>
                    </span>
                  ) : (
                    <Link className="text-primary hover:underline" to={pagePath(piece.uuid)}>
                      #{piece.id}
                    </Link>
                  )}
                </TableCell>
                <TableCell>{quantity(piece.quantity)}</TableCell>
                <TableCell>
                  <StockStatusCodeBadge code={piece.status} />
                </TableCell>
                <TableCell>{formatDate(piece.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
